import React, { Fragment } from 'react'
import { graphql } from 'gatsby'
import { keyframes } from '@emotion/core'
import Img from 'gatsby-image'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
  dropShadow,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, min, rem, selection, lines } from '../../styles/tools'

import { AutoFade, AutoSlide, AutoScale } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import Link from '../../components/Link'
import { Headline, BoldText, Detail } from '../../styles/typography'

import Logo from '../../assets/pages/work/turnkey/Logo.js'

const rotateBall = keyframes({ to: { transform: 'rotate(360deg)' } })

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/turnkey.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/turnkey.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/turnkey.jpg" }) {
      ...twitterShareImage
    }
    macbook: file(relativePath: { eq: "pages/work/turnkey/macbook.png" }) {
      ...smallImage
    }
    iphone: file(relativePath: { eq: "pages/work/turnkey/iphone.png" }) {
      ...smallImage
    }
    webpage: file(relativePath: { eq: "pages/work/turnkey/webpage.jpg" }) {
      ...smallImage
    }
    webpageAfter: file(
      relativePath: { eq: "pages/work/turnkey/webpage-after.jpg" }
    ) {
      ...smallImage
    }
    webpageBefore: file(
      relativePath: { eq: "pages/work/turnkey/webpage-before.jpg" }
    ) {
      ...smallImage
    }
    corporateLogos: file(
      relativePath: { eq: "pages/work/turnkey/corporate-logos.png" }
    ) {
      ...smallImage
    }
    fbImage: file(
      relativePath: { eq: "pages/work/turnkey/facebook-image.png" }
    ) {
      ...smallImage
    }
    facebook: file(relativePath: { eq: "pages/work/turnkey/facebook.jpg" }) {
      ...smallImage
    }
    footballParallax: file(
      relativePath: { eq: "pages/work/turnkey/football-parallax.png" }
    ) {
      ...smallImage
    }
    instagramComments: file(
      relativePath: { eq: "pages/work/turnkey/instagram-comments.jpg" }
    ) {
      ...smallImage
    }
    instagramTop: file(
      relativePath: { eq: "pages/work/turnkey/instagram-top.jpg" }
    ) {
      ...smallImage
    }
    parallaxBg: file(
      relativePath: { eq: "pages/work/turnkey/parallax-bg.jpg" }
    ) {
      ...smallImage
    }
    socialImage: file(
      relativePath: { eq: "pages/work/turnkey/social-image.jpg" }
    ) {
      ...smallImage
    }
    stackCenter: file(
      relativePath: { eq: "pages/work/turnkey/stack-center.jpg" }
    ) {
      ...smallImage
    }
    stackLeft: file(relativePath: { eq: "pages/work/turnkey/stack-left.jpg" }) {
      ...smallImage
    }
    stackRight: file(
      relativePath: { eq: "pages/work/turnkey/stack-right.jpg" }
    ) {
      ...smallImage
    }
  }
`

let project = getProject('turnkey')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo play width={360} />,
  }

  const macbook = data.macbook.childImageSharp.fluid
  const iphone = data.iphone.childImageSharp.fluid
  const webpage = data.webpage.childImageSharp.fluid
  const webpageAfter = data.webpageAfter.childImageSharp.fluid
  const webpageBefore = data.webpageBefore.childImageSharp.fluid
  const corporateLogos = data.corporateLogos.childImageSharp.fluid
  const fbImage = data.facebookImage.childImageSharp.fluid
  const facebook = data.facebook.childImageSharp.fluid
  const footballParallax = data.footballParallax.childImageSharp.fluid
  const instagramComments = data.instagramComments.childImageSharp.fluid
  const instagramTop = data.instagramTop.childImageSharp.fluid
  const parallaxBg = data.parallaxBg.childImageSharp.fluid
  const socialImage = data.socialImage.childImageSharp.fluid
  const stackCenter = data.stackCenter.childImageSharp.fluid
  const stackLeft = data.stackLeft.childImageSharp.fluid
  const stackRight = data.stackRight.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <AutoFade>
            <Detail css={{ opacity: 0.5 }}>CHALLENGE:</Detail>
            <Space y={16} />
            <Title as="h1">
              Fresh off an acquisition with seconds on the clock, we helped the
              experts in sports and entertainment research score big by
              improving their digital game.
            </Title>
          </AutoFade>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <div
          css={{
            width: '115%',
            position: 'relative',
            margin: 'auto',
            marginBottom: '-30vmin',
            marginLeft: '-9%',
          }}
        >
          <div css={{ width: '89%' }}>
            <AutoSlide up style={dropShadow()}>
              <Img fluid={macbook} />
            </AutoSlide>
          </div>
          <div
            css={{
              width: '19%',
              position: 'absolute',
              top: '26%',
              right: '8%',
            }}
          >
            <AutoSlide up delay={1 * 300} style={dropShadow()}>
              <Img fluid={iphone} />
            </AutoSlide>
          </div>
        </div>
      </ProjectIntro>
      <Wrapper padded css={{ minHeight: '100vh' }}>
        <Space y="40vmin" />
        Content
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <div
            css={[
              lines('rgba(255, 255, 255, .06)'),
              {
                display: 'flex',
                alignItems: 'center',
                height: '130%',
                userSelect: 'none',
                backgroundColor: project.color,
              },
            ]}
          >
            <div
              css={{
                width: '200%',
                [min(1200)]: { width: '60%' },
                position: 'relative',
                top: '1%',
              }}
            >
              <svg viewBox="0 0 58.2 36" fill={project.colors[0]}>
                <path d="M49.8 0L33.3 20.2a11.2 11.2 0 0 0-3.8-5L10.9 0H0v36h6v-.2l.2.2 5.8-7.2C13.8 33 17.9 36 22.6 36c3.2 0 6.2-1.4 8.3-3.6v.1l21.3-26V36h6V0h-8.4zm-37 18.4L6 26.7V6h2.9v-.3.3l8.4 7-2.5 3c-.8.7-1.4 1.5-2 2.4zM22.6 30c-3 0-5.5-2.5-5.5-5.5s2.5-5.5 5.5-5.5 5.5 2.5 5.5 5.5-2.4 5.5-5.5 5.5z" />
              </svg>
            </div>
          </div>
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              alignItems: 'center',
              height: '65vh',
              maxHeight: rem(600),
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title
              as="blockquote"
              css={{ maxWidth: '21em', textAlign: 'center' }}
            >
              The new Turnkey identity and website debuted at Sports Business
              Week 2018, the launch was celebrated as a success by all
              stakeholders of the MarketCast Group.
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
